/**
 * Viewport-aware placement for portal popups (dropdowns, pickers).
 *
 * Anchors a floating panel to a trigger's measured window rect. Opens below
 * the trigger when there is room, flips above when there isn't, and clamps
 * into the viewport as a last resort — so the popup for the LAST element on a
 * page is never cut off with no way to scroll it into view.
 */
export interface TriggerLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PanelPosition {
  top: number;
  left: number;
}

const MARGIN = 8;

/**
 * @param layout    measured window rect of the trigger
 * @param panelW    panel width in px
 * @param panelH    panel height (or an upper-bound estimate) in px
 * @param alignEnd  right-align the panel to the trigger's end edge (RTL menus).
 *                  When false the panel's left edge matches the trigger's.
 */
export function computePortalPosition(
  layout: TriggerLayout,
  panelW: number,
  panelH: number,
  alignEnd = false,
): PanelPosition {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  let left = alignEnd ? layout.x + layout.width - panelW : layout.x;
  left = Math.max(MARGIN, Math.min(left, vw - panelW - MARGIN));

  const below = layout.y + layout.height + 4;
  const above = layout.y - panelH - 4;
  let top: number;
  if (below + panelH <= vh - MARGIN) {
    top = below; // fits below the trigger
  } else if (above >= MARGIN) {
    top = above; // flip above
  } else {
    top = Math.max(MARGIN, vh - panelH - MARGIN); // clamp into view
  }
  return { top, left };
}
